/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useState, ReactNode, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import "./custom-tooltip-portal.css";

interface CustomTooltipProps {
  content: string;
  children: ReactNode;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ content, children }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ x: rect.right + 8, y: rect.top + rect.height / 2 });
    }
  }, [visible]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ x: rect.right + 8, y: rect.top + rect.height / 2 });
    }
  };

  return (
    <div
      ref={triggerRef}
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={e => { e.stopPropagation(); setVisible(v => !v); }}
      onMouseMove={handleMouseMove}
    >
      {children}
      {visible &&
        ReactDOM.createPortal(
          <div
            className="custom-tooltip-portal"
            style={{ left: coords.x, top: coords.y, transform: "translateY(-50%)" }}
          >
            <div className="custom-tooltip-portal-content" dangerouslySetInnerHTML={{ __html: content }} />
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomTooltip;